import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import './Hero.css';

export default function Hero({ theme = 'night' }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  const sunY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const sunScale = useTransform(scrollYProgress, [0, 1], [1, 1.25]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let ctx;
    try { ctx = canvas.getContext('2d'); } catch { return; }
    if (!ctx) return;
    let raf;
    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
    };
    resize();
    window.addEventListener('resize', resize);

    const particles = Array.from({ length: 60 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 1.6 + 0.3,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      a: Math.random() * 0.6 + 0.2,
    }));

    const readParticleColor = () => {
      const v = getComputedStyle(document.documentElement)
        .getPropertyValue('--particle-color').trim();
      return v || '255, 220, 140';
    };
    let particleColor = readParticleColor();
    const refreshColor = () => { particleColor = readParticleColor(); };
    const observer = new MutationObserver(refreshColor);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of particles) {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = canvas.width; if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height; if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * window.devicePixelRatio, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${particleColor}, ${p.a})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      window.removeEventListener('resize', resize);
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section id="top" className="hero" ref={ref}>
      <canvas ref={canvasRef} className="hero-particles" />

      <motion.div className="hero-sun-wrap" style={{ y: sunY, scale: sunScale }}>
        {theme === 'night' ? (
          <div className="hero-moon">
            <div className="hero-moon-glow" />
            <div className="hero-moon-core" />
            <div className="hero-moon-craters" />
            <div className="hero-moon-ring r1" />
            <div className="hero-moon-ring r2" />
          </div>
        ) : (
          <div className="hero-sun">
            <div className="hero-sun-core" />
            <div className="hero-sun-corona" />
            <div className="hero-sun-ring r1" />
            <div className="hero-sun-ring r2" />
            <div className="hero-sun-ring r3" />
          </div>
        )}
      </motion.div>

      <div className="hero-grid" aria-hidden />

      <motion.div
        className="container hero-content"
        style={{ y: contentY, opacity: contentOpacity }}
      >
        <motion.span
          className="eyebrow"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          Powering a cleaner tomorrow
        </motion.span>

        <motion.h1
          className="hero-title"
          initial="hidden"
          animate="visible"
          variants={{
            visible: { transition: { staggerChildren: 0.08, delayChildren: 0.3 } },
          }}
        >
          {['Harness', 'the', 'Sun.'].map((w, i) => (
            <motion.span
              key={i}
              className="word"
              variants={{
                hidden: { y: '100%', opacity: 0 },
                visible: { y: 0, opacity: 1, transition: { duration: 0.7, ease: [0.2, 0.7, 0.2, 1] } },
              }}
            >
              {w}{' '}
            </motion.span>
          ))}
          <br />
          <motion.span
            className="word accent"
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.8, ease: [0.2, 0.7, 0.2, 1] }}
          >
            Power the World.
          </motion.span>
        </motion.h1>

        <motion.p
          className="hero-lead"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.0 }}
        >
          Global Sun Energy Solution engineers next-generation solar panels, inverters, and
          storage systems — delivering clean, reliable power to homes, industries, and
          communities across India.
        </motion.p>

        <motion.div
          className="hero-ctas"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          <a href="#products" className="btn btn-primary">Explore Products <span aria-hidden>→</span></a>
          <a href="#contact" className="btn btn-ghost">Talk to an Expert</a>
        </motion.div>

        <motion.div
          className="hero-chips"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.4 }}
        >
          <div className="chip"><strong>2.4 GW</strong><span>Installed Capacity</span></div>
          <div className="chip"><strong>28+</strong><span>States Served</span></div>
          <div className="chip"><strong>25-yr</strong><span>Performance Warranty</span></div>
        </motion.div>
      </motion.div>

      <motion.div
        className="hero-scroll"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
      >
        <span>Scroll</span>
        <div className="scroll-line"><span /></div>
      </motion.div>
    </section>
  );
}
